import type { MouseEvent } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FilterChip } from '../utils/issueListFilters';

const PHONE_LIMIT = 2;

interface ActiveFilterChipsProps {
  chips: FilterChip[];
  onOpen: () => void;
  onRemove: (field: FilterChip['field']) => void;
  onClear: () => void;
  tabChip?: { label: string; value: string; onReset: () => void };
}

export const ActiveFilterChips = ({ chips, onOpen, onRemove, onClear, tabChip }: ActiveFilterChipsProps) => {
  const { t } = useTranslation();

  if (chips.length === 0 && !tabChip) return null;

  const items = [
    ...(tabChip ? [{ key: 'tab', label: tabChip.label, value: tabChip.value, phoneOnly: true, remove: tabChip.onReset }] : []),
    ...chips.map(({ field, label, value }) => ({ key: field, label, value, phoneOnly: false, remove: () => onRemove(field) })),
  ];

  // Move focus to the neighbour chip: the removed one unmounts (the last one takes the row along).
  const handleRemove = (event: MouseEvent<HTMLButtonElement>, remove: () => void) => {
    const item = event.currentTarget.closest('li');
    const next =
      item?.nextElementSibling?.querySelector<HTMLButtonElement>('button:last-of-type') ??
      item?.previousElementSibling?.querySelector<HTMLButtonElement>('button:last-of-type');
    remove();
    next?.focus();
  };

  return (
    <div className={`mb-4 flex flex-wrap items-center gap-2 ${chips.length === 0 ? 'sm:hidden' : ''}`}>
      <ul className="flex min-w-0 items-center gap-2 max-sm:w-full max-sm:grid max-sm:grid-flow-col max-sm:auto-cols-[minmax(0,max-content)] sm:flex-wrap">
        {items.map(({ key, label, value, phoneOnly, remove }, index) => (
          <li
            key={key}
            className={`${phoneOnly ? 'sm:hidden' : ''} ${index >= PHONE_LIMIT ? 'max-sm:hidden' : ''} flex min-h-8 min-w-0 items-center rounded-full border border-gray-200 bg-gray-50 text-sm`}
          >
            <button
              type="button"
              onClick={onOpen}
              aria-haspopup="dialog"
              className="flex min-h-8 min-w-0 items-center gap-1.5 rounded-l-full py-1 pl-3 pr-1 transition-colors hover:bg-gray-100"
            >
              <span className="shrink-0 text-gray-500">{label}</span>{' '}
              <span className="min-w-0 max-w-[140px] truncate font-medium text-gray-900">{value}</span>
            </button>
            <button
              type="button"
              onClick={(event) => handleRemove(event, remove)}
              aria-label={t('Remove {{label}}', { label: `${label} : ${value}` })}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-200"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </li>
        ))}
        {items.length > PHONE_LIMIT && (
          <li className="flex min-h-8 shrink-0 items-center rounded-full border border-gray-200 bg-gray-50 text-sm sm:hidden">
            <button
              type="button"
              onClick={onOpen}
              aria-haspopup="dialog"
              aria-label={t('{{count}} more', { count: items.length - PHONE_LIMIT })}
              className="flex min-h-8 items-center rounded-full px-3 font-medium text-gray-900 transition-colors hover:bg-gray-100"
            >
              …
            </button>
          </li>
        )}
      </ul>
      {chips.length >= 2 && (
        <button
          type="button"
          onClick={onClear}
          className="min-h-[24px] px-1 text-xs text-gray-500 hover:text-gray-700 hover:underline"
        >
          {t('Clear filters')}
        </button>
      )}
    </div>
  );
};
